<template>
  <div>
    <div class="mt-5 flex flex-wrap items-baseline gap-2.5">
      <h2 class="text-base font-medium">Full matches</h2>
      <span class="text-xs text-gray-500" data-cy="conflict-full-count">
        {{ fullMatches.length }}
      </span>
      <span class="ml-auto text-xs text-gray-500">
        Open a result for contact details and every field searched
      </span>
    </div>

    <div
      v-if="fullMatches.length"
      class="overflow-hidden rounded border border-gray-200 bg-white"
      data-cy="conflict-full-matches"
    >
      <!-- ll-standards-ignore LL091 -- one record can match on the same field twice,
           so recordId+field is not unique; the tier is replaced wholesale per run -->
      <ConflictResultRow
        v-for="(row, index) in fullMatches"
        :key="`${row.recordId}-${index}`"
        :row="row"
        :grouped="isGrouped(fullMatches, index)"
        @open="emit('open', $event)"
      />
    </div>
    <!-- A plain note rather than LlWarningBanner: that atom carries `no-print`, and
         "no full matches" is exactly the sentence a printed sheet must not lose. -->
    <div
      v-else
      class="rounded border border-amber-200 bg-amber-50 px-3.5 py-3 text-xs text-gray-800"
      data-cy="conflict-no-full-matches"
    >
      No full matches. Everything below matched only some of the words in its
      name.
    </div>

    <!--
      Partial matches are always present. They are collapsed only when there are full
      matches to read first; with no full matches they are the closest thing to an
      answer and open by default.
    -->
    <v-expansion-panels
      v-model="expanded"
      class="mt-5"
      variant="accordion"
      data-cy="conflict-partial-panel"
    >
      <v-expansion-panel value="partial">
        <v-expansion-panel-title>
          <span class="text-sm">
            Partial matches
            <span
              class="ml-1.5 text-xs text-gray-500"
              data-cy="conflict-partial-count"
            >
              {{ partialMatches.length }}
            </span>
          </span>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <p class="mb-3 max-w-[76ch] text-xs text-gray-500">
            <template v-if="!fullMatches.length && partialMatches.length">
              <span class="font-medium text-gray-700">
                No full matches were found. These are the closest.
              </span>
              Some but not all of the words in a name matched.
            </template>
            <template v-else>
              Some but not all of the words in a name matched. Always here,
              never hidden — closest first.
            </template>
          </p>
          <div
            v-if="partialMatches.length"
            class="overflow-hidden rounded border border-gray-200 bg-white"
            data-cy="conflict-partial-matches"
          >
            <!-- ll-standards-ignore LL091 -- as above: recordId+field repeats, and the
                 tier is replaced wholesale per run -->
            <ConflictResultRow
              v-for="(row, index) in partialMatches"
              :key="`${row.recordId}-${index}`"
              :row="row"
              show-fraction
              :grouped="isGrouped(partialMatches, index)"
              @open="emit('open', $event)"
            />
          </div>
          <p v-else class="text-xs text-gray-500">None.</p>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import ConflictResultRow from './ConflictResultRow.vue';
import type { ConflictResultRow as ConflictRow } from '../models/conflictTypes';

const props = defineProps<{
  fullMatches: ConflictRow[];
  partialMatches: ConflictRow[];
}>();

const emit = defineEmits<{ open: [row: ConflictRow] }>();

const expanded = ref<string | undefined>(
  props.fullMatches.length ? undefined : 'partial',
);

watch(
  () => props.fullMatches.length,
  (count) => {
    expanded.value = count ? undefined : 'partial';
  },
);

/** Consecutive rows for the same record are one finding shown from several fields. */
function isGrouped(rows: ConflictRow[], index: number): boolean {
  return index > 0 && rows[index - 1].recordId === rows[index].recordId;
}

/** The page calls this before printing — a collapsed panel prints as nothing. */
function expandPartials(): void {
  expanded.value = 'partial';
}

defineExpose({ expandPartials });
</script>
